"use client";
import { TInquiriesDetailsData } from "@/interfaces";
import { getDynamicComponentById } from "@/lib";
import {
  isActiveLoansInquiryDetailsComponent,
  isBankValidationDetailsComponent,
  isBouncedCheckInquiryDetailsComponent,
  isCarDocumentDetailsComponent,
  isCardsInquiryDetailsComponent,
  isCardToDepositInquiryDetailsComponent,
  isCardToIbanInquiryDetailsComponent,
  isChequeDetailsComponent,
  isChequeTransferChainsDetailsComponent,
  isCompanyInfoInquiryDetailsComponent,
  isTaxRecordsInquiryDetailsComponent,
  isCompanyLicensesInquiryDetailsComponent,
  isCompanyMembersInquiryDetailsComponent,
  isCompanyNewspaperDetailsComponent,
  isCompanyParentsInquiryDetailsComponent,
  isCorporateIdentityInquiryDetailsComponent,
  isDepositToIbanInquiryDetailsComponent,
  isEducationalExemptionInquiryDetailsComponent,
  isElectricityBillDetailsComponent,
  isElectricityBillInfoDetailsComponent,
  isElectricityHistoryDetailsComponent,
  isElectricityImageDetailsComponent,
  isFinesInquiryDetailsComponent,
  isFinesPhotoDetailsComponent,
  isFreewayTollDetailsComponent,
  isVehicleInfoDetailsComponent,
  isForeignersDetailsComponent,
  isGasBillDetailsComponent,
  isGasBillInfoDetailsComponent,
  isGasHistoryDetailsComponent,
  isIbanInquiryDetailsComponent,
  isIdentityInquiryDetailsComponent,
  isMilitaryCardInquiryDetailsComponent,
  isMilitaryServiceInquiryDetailsComponent,
  isMobileMciDetailsComponent,
  isMobileMtnDetailsComponent,
  isMobileRightelDetailsComponent,
  isMotorcycleFinesInquiryDetailsComponent,
  isNegativePointsInquiryDetailsComponent,
  isDrivingLicenseStatusInquiryDetailsComponent,
  isPassportStatusInquiryDetailsComponent,
  isPhoneBillDetailsComponent,
  isPhoneBillImageDetailsComponent,
  isPlateInquiryDetailsComponent,
  isPostalCodeInquiryDetailsComponent,
  isPostalTrackingDetailsComponent,
  isRegisterChequeDetailsComponent,
  isSanaInquiryDetailsComponent,
  isThirdPartyInsuranceDetailsComponent,
  isVehicleTransferTaxDetailsComponent,
  isWaterBillDetailsComponent,
  isWaterBillInfoDetailsComponent,
} from "@/lib/guards";
import { ComponentType, JSX } from "react";
import { globalRawServicesData } from "./globalRawServicesData/globalRawServicesData";
import { IRawPurchasedBankValidationResData } from "@/interfaces/inquiries/creditScore";
import {
  IRawPurchasedBouncedCheckInquiryResData,
  IRawPurchasedActiveLoansInquiryResData,
  IRawPurchasedCardsInquiryResData,
  IRawPurchasedCardToDepositInquiryResData,
  IRawPurchasedCardToIbanInquiryResData,
  IRawPurchasedDepositToIbanInquiryResData,
  IRawPurchasedIbanInquiryResData,
} from "@/interfaces/inquiries/bank";
import {
  IRawPurchasedCompanyInfoInquiryResData,
  IRawPurchasedCompanyLicensesInquiryResData,
  IRawPurchasedCompanyMembersInquiryResData,
  IRawPurchasedCompanyNewspaperInquiryResData,
  IRawPurchasedCompanyParentsInquiryResData,
  IRawPurchasedCorporateIdentityInquiryResData,
  IRawPurchasedTaxRecordsInquiryResData,
} from "@/interfaces/inquiries/legal";
import {
  IRawPurchasedEducationalExemptionInquiryResData,
  IRawPurchasedElectricityHistoryInquiryResData,
  IRawPurchasedElectricityImageInquiryResData,
  IRawPurchasedElectricityInfoInquiryResData,
  IRawPurchasedElectricityInquiryResData,
  IRawPurchasedForeignersInquiryResData,
  IRawPurchasedGasBillHistoryInquiryResData,
  IRawPurchasedGasInfoInquiryResData,
  IRawPurchasedGasInquiryResData,
  IRawPurchasedIdentityInquiryResData,
  IRawPurchasedMilitaryCardInquiryResData,
  IRawPurchasedMilitaryServiceInquiryResData,
  IRawPurchasedPassportStatusInquiryResData,
  IRawPurchasedPostalCodeInquiryResData,
  IRawPurchasedPostalTrackingInquiryResData,
  IRawPurchasedSanaInquiryResData,
  IRawPurchasedWaterInfoInquiryResData,
  IRawPurchasedWaterInquiryResData,
} from "@/interfaces/inquiries/governmental";
import {
  IRawPurchasedCarDocumentResData,
  IRawPurchasedFreewayTollInquiryResData,
  IRawPurchasedThirdPartyInsuranceInquiryResData,
  IRawPurchasedVehicleInfoInquiryResData,
  IRawPurchasedMotorcycleFinesResData,
  IRawPurchasedNegativePointsInquiryResData,
  IRawPurchasedDrivingLicenseStatusInquiryResData,
  IRawPurchasedPlateInquiryResData,
  IRawPurchasedTrafficFinesPhotoResData,
  IRawPurchasedTrafficFinesResData,
  IRawPurchasedVehicleTransferTaxInquiryResData,
} from "@/interfaces/inquiries/vehicle";
import {
  IRawPurchasedCheckInquiryResData,
  IRawPurchasedChequeTransfersChainInquiryResData,
  IRawPurchasedRegisterCheckInquiryResData,
} from "@/interfaces/inquiries/chequeInquiry";

const createResolver = <T extends TInquiriesDetailsData>(
  id: number,
  guardFn: (data?: TInquiriesDetailsData) => boolean,
) => ({
  guard: guardFn,
  render: async (data: any): Promise<JSX.Element | null> => {
    const Comp: ComponentType<{ data: T }> | null =
      await getDynamicComponentById(id);

    return Comp ? <Comp data={data} /> : null;
  },
});
export const componentRenderers = [
  createResolver<IRawPurchasedIbanInquiryResData>(
    globalRawServicesData.Iban_Inquiry.api.apiId,
    isIbanInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedCardsInquiryResData>(
    globalRawServicesData.Card_Inquiry.api.apiId,
    isCardsInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedCardToDepositInquiryResData>(
    globalRawServicesData.Card_To_Account.api.apiId,
    isCardToDepositInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedCardToIbanInquiryResData>(
    globalRawServicesData.Card_To_Iban.api.apiId,
    isCardToIbanInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedDepositToIbanInquiryResData>(
    globalRawServicesData.Account_To_Iban.api.apiId,
    isDepositToIbanInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedActiveLoansInquiryResData>(
    globalRawServicesData.Active_Loans.api.apiId,
    isActiveLoansInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedBouncedCheckInquiryResData>(
    globalRawServicesData.Bounced_Check.api.apiId,
    isBouncedCheckInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedThirdPartyInsuranceInquiryResData>(
    globalRawServicesData.Third_Party_Insurance.api.apiId,
    isThirdPartyInsuranceDetailsComponent,
  ),
  createResolver<IRawPurchasedCompanyInfoInquiryResData>(
    globalRawServicesData.Company_Information_Inquiry.api.apiId,
    isCompanyInfoInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedTaxRecordsInquiryResData>(
    globalRawServicesData.Tax_Records_Inquiry.api.apiId,
    isTaxRecordsInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedMilitaryServiceInquiryResData>(
    globalRawServicesData.Military_Service_Status.api.apiId,
    isMilitaryServiceInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedMilitaryCardInquiryResData>(
    globalRawServicesData.Military_Card_Status.api.apiId,
    isMilitaryCardInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedEducationalExemptionInquiryResData>(
    globalRawServicesData.Educational_Exemption.api.apiId,
    isEducationalExemptionInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedPostalCodeInquiryResData>(
    globalRawServicesData.Postal_Code_Inquiry.api.apiId,
    isPostalCodeInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedTrafficFinesResData>(
    globalRawServicesData.Fines.api.apiId,
    isFinesInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedVehicleTransferTaxInquiryResData>(
    globalRawServicesData.Vehicle_Transfer_Tax.api.apiId,
    isVehicleTransferTaxDetailsComponent,
  ),
  createResolver<IRawPurchasedFreewayTollInquiryResData>(
    globalRawServicesData.Freeway_Toll.api.apiId,
    isFreewayTollDetailsComponent,
  ),
  createResolver<IRawPurchasedVehicleInfoInquiryResData>(
    globalRawServicesData.Vehicle_Info.api.apiId,
    isVehicleInfoDetailsComponent,
  ),
  createResolver<IRawPurchasedMotorcycleFinesResData>(
    globalRawServicesData.Motorcycle_Fines.api.apiId,
    isMotorcycleFinesInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedPlateInquiryResData>(
    globalRawServicesData.Plate_Inquiry.api.apiId,
    isPlateInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedNegativePointsInquiryResData>(
    globalRawServicesData.Negative_Points.api.apiId,
    isNegativePointsInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedDrivingLicenseStatusInquiryResData>(
    globalRawServicesData.Driving_License_Status.api.apiId,
    isDrivingLicenseStatusInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedPassportStatusInquiryResData>(
    globalRawServicesData.Passport_Status.api.apiId,
    isPassportStatusInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedSanaInquiryResData>(
    globalRawServicesData.Sana_Inquiry.api.apiId,
    isSanaInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedIdentityInquiryResData>(
    globalRawServicesData.Identity_Inquiry.api.apiId,
    isIdentityInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedBankValidationResData>(
    globalRawServicesData.Bank_Validation.api.apiId,
    isBankValidationDetailsComponent,
  ),
  createResolver<IRawPurchasedForeignersInquiryResData>(
    globalRawServicesData.Foreigners_Inquiry.api.apiId,
    isForeignersDetailsComponent,
  ),
  createResolver<IRawPurchasedCompanyMembersInquiryResData>(
    globalRawServicesData.Company_Members_Inquiry.api.apiId,
    isCompanyMembersInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedCompanyLicensesInquiryResData>(
    globalRawServicesData.Company_Licenses_Inquiry.api.apiId,
    isCompanyLicensesInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedCompanyParentsInquiryResData>(
    globalRawServicesData.Company_Parent_Inquiry.api.apiId,
    isCompanyParentsInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedCorporateIdentityInquiryResData>(
    globalRawServicesData.Corporation_Identity_Inquiry.api.apiId,
    isCorporateIdentityInquiryDetailsComponent,
  ),
  createResolver<IRawPurchasedTrafficFinesPhotoResData>(
    globalRawServicesData.Fines_Photo.api.apiId,
    isFinesPhotoDetailsComponent,
  ),
  createResolver<IRawPurchasedCarDocumentResData>(
    globalRawServicesData.Car_Document_Inquiry.api.apiId,
    isCarDocumentDetailsComponent,
  ),
  createResolver<IRawPurchasedRegisterCheckInquiryResData>(
    globalRawServicesData.Cheque_Inquiry.api.apiId,
    isRegisterChequeDetailsComponent,
  ),
  createResolver<IRawPurchasedCheckInquiryResData>(
    globalRawServicesData.Cheque_V2_Inquiry.api.apiId,
    isChequeDetailsComponent,
  ),
  createResolver<IRawPurchasedChequeTransfersChainInquiryResData>(
    globalRawServicesData.Transfer_Chain_Inquiry.api.apiId,
    isChequeTransferChainsDetailsComponent,
  ),
  createResolver<IRawPurchasedPostalTrackingInquiryResData>(
    globalRawServicesData.Postal_Tracking_Inquiry.api.apiId,
    isPostalTrackingDetailsComponent,
  ),
  createResolver<IRawPurchasedCompanyNewspaperInquiryResData>(
    globalRawServicesData.Company_Newspaper_inquiry.api.apiId,
    isCompanyNewspaperDetailsComponent,
  ),
  createResolver<IRawPurchasedElectricityInquiryResData>(
    globalRawServicesData.Electricity_Bill_inquiry.api.apiId,
    isElectricityBillDetailsComponent,
  ),
  createResolver<IRawPurchasedElectricityInfoInquiryResData>(
    globalRawServicesData.Electricity_Bill_Info_inquiry.api.apiId,
    isElectricityBillInfoDetailsComponent,
  ),
  createResolver<IRawPurchasedGasInquiryResData>(
    globalRawServicesData.Gas_Bill_inquiry.api.apiId,
    isGasBillDetailsComponent,
  ),
  createResolver<IRawPurchasedGasInfoInquiryResData>(
    globalRawServicesData.Gas_Bill_Info_inquiry.api.apiId,
    isGasBillInfoDetailsComponent,
  ),
  createResolver<IRawPurchasedWaterInquiryResData>(
    globalRawServicesData.Water_Bill_inquiry.api.apiId,
    isWaterBillDetailsComponent,
  ),
  createResolver<IRawPurchasedWaterInfoInquiryResData>(
    globalRawServicesData.Water_Bill_Info_inquiry.api.apiId,
    isWaterBillInfoDetailsComponent,
  ),

  createResolver<IRawPurchasedElectricityHistoryInquiryResData>(
    globalRawServicesData.Electricity_Bill_History_inquiry.api.apiId,
    isElectricityHistoryDetailsComponent,
  ),
  createResolver<IRawPurchasedElectricityImageInquiryResData>(
    globalRawServicesData.Electricity_Bill_Image_inquiry.api.apiId,
    isElectricityImageDetailsComponent,
  ),
  createResolver<IRawPurchasedGasBillHistoryInquiryResData>(
    globalRawServicesData.Gas_Bill_History_inquiry.api.apiId,
    isGasHistoryDetailsComponent,
  ),
  createResolver<IRawPurchasedWaterInfoInquiryResData>(
    globalRawServicesData.Phone_Bill_inquiry.api.apiId,
    isPhoneBillDetailsComponent,
  ),
  createResolver<IRawPurchasedWaterInfoInquiryResData>(
    globalRawServicesData.Phone_Bill_Image_inquiry.api.apiId,
    isPhoneBillImageDetailsComponent,
  ),
  createResolver<IRawPurchasedWaterInfoInquiryResData>(
    globalRawServicesData.Mobile_Bill_Mtn_inquiry.api.apiId,
    isMobileMtnDetailsComponent,
  ),
  createResolver<IRawPurchasedWaterInfoInquiryResData>(
    globalRawServicesData.Mobile_Bill_Mci_inquiry.api.apiId,
    isMobileMciDetailsComponent,
  ),
  createResolver<IRawPurchasedWaterInfoInquiryResData>(
    globalRawServicesData.Mobile_Bill_Rightel_inquiry.api.apiId,
    isMobileRightelDetailsComponent,
  ),
];
